// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {
  components,
  ControlProps,
  MenuProps,
  OptionProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';

import {PremiumFeatureTooltip} from '../../../../util/accounts/pricing';
import {RegistryRole, RegistryRoles} from './types';

export type RegistryRoleOption = {
  value: RegistryRole;
  showPremiumFeatureIcon: boolean;
  premiumFeatureEnabled: boolean;
  isDisabled: boolean;
};

export function getRegistryRoleOptionFromRole(
  role: RegistryRole,
  isPremiumFeatureEnabled: boolean
): RegistryRoleOption {
  if (role === RegistryRoles.Admin) {
    return {
      value: role,
      showPremiumFeatureIcon: false,
      premiumFeatureEnabled: isPremiumFeatureEnabled,
      isDisabled: false,
    };
  }
  return {
    value: role,
    showPremiumFeatureIcon: true,
    premiumFeatureEnabled: isPremiumFeatureEnabled,
    isDisabled: !isPremiumFeatureEnabled,
  };
}

export function CheckmarkSelectOption({
  children,
  ...props
}: OptionProps<RegistryRoleOption, false>) {
  return (
    <components.Option
      {...props}
      className="flex items-center justify-between pr-[10px]">
      <span className="mr-[10px]">{children}</span>
      <div className="flex items-center gap-[10px]">
        {props.isSelected && <Icon name="checkmark" width={18} height={18} />}
        {props.data.showPremiumFeatureIcon && (
          <PremiumFeatureTooltip
            isPremiumFeatureDisabled={!props.data.premiumFeatureEnabled}
          />
        )}
      </div>
    </components.Option>
  );
}

export function CustomSingleValue({
  children,
  isRegistryOwner,
  augmentedRole,
  ...props
}: SingleValueProps<RegistryRoleOption, false> & {
  isRegistryOwner: boolean;
  augmentedRole?: RegistryRole;
}) {
  return (
    <components.SingleValue {...props}>
      <RegistryRolePill
        role={props.getValue()[0].value}
        showDropdownIndicator={true}
        isRegistryOwner={isRegistryOwner}
        augmentedRole={augmentedRole}
      />
    </components.SingleValue>
  );
}

export const RegistryRolePill = ({
  role,
  showDropdownIndicator,
  isRegistryOwner,
  augmentedRole,
}: {
  role: RegistryRole;
  showDropdownIndicator?: boolean;
  isRegistryOwner?: boolean;
  augmentedRole?: RegistryRole;
}) => {
  const showRoleInfo = isRegistryOwner || augmentedRole;
  return (
    <Tailwind className="flex items-center gap-2 font-semibold text-moon-800">
      <Tag
        color="moon"
        label={role}
        endIconName={showDropdownIndicator ? 'chevron-down' : undefined}
      />
      {showRoleInfo && (
        <span className="ml-4 font-normal text-moon-500">
          {isRegistryOwner && 'Owner'}
          {augmentedRole &&
            `${isRegistryOwner ? '. ' : ''}${augmentedRole} through team(s)`}
        </span>
      )}
    </Tailwind>
  );
};

export const customSelectComponents = {
  Control: (props: ControlProps<RegistryRoleOption, false>) => (
    <components.Control
      {...props}
      className="-ml-2 w-fit bg-transparent shadow-none outline-none hover:cursor-pointer hover:shadow-none hover:outline-none focus:outline-none"
    />
  ),
  ValueContainer: (props: ValueContainerProps<RegistryRoleOption, false>) => (
    <components.ValueContainer {...props} className="p-0" />
  ),
  Menu: (props: MenuProps<RegistryRoleOption, false>) => (
    <Tailwind>
      <components.Menu {...props} className="w-max min-w-[8rem]" />
    </Tailwind>
  ),
  Option: CheckmarkSelectOption,
  DropdownIndicator: () => null,
};
